import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import {
  Card,
  TextField,
  MenuItem,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import Preloader from '../../../../components/other/Preloader/Preloader';
import { Modal } from '../../../../components/other/Modals';
import { IAppState } from '../../../../store/rootDuck';
import { actions as usersActions } from '../../../../store/ducks/users.duck';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import { usersBreadCrumbs } from './utils/createBreadCrumbs';
import homeStyles from '../../../../constants/homeStyles';
import { useStylesEditPage } from './hooks/useStyleEditPage';
import {
  useFormatMessage,
} from '../../../../hooks';
import { useFormikUserEdit } from './hooks/useFormikUserEdit'

interface IProps {
  id?: string
  type: string
  setUser?: (user: string) => void
  setUserOpen: (userOpen: boolean) => void
}

const UsersEditPage: React.FC<TPropsFromRedux & IProps> = ({
  user,
  fetch,
  page,
  clearFetchUser,
  loading,
  clearEdit,
  add,
  edit,
  delUser,
  clearDelUser,
  delUserSuccess,
  delUserError,
  delUserLoading,
  editSuccess,
  editLoading,
  editError,
  byIdError,
  clearPagination,
  addSuccess,
  addUserId,
  clearAddUserId,
  setUser,
  id,
  type,
  setUserOpen
}) => {
  // const { userId, type } = useParams();
  // const id = userId;
  const [open, setOpen] = useState(false);
  const fm = useFormatMessage();

  const classes = useStylesEditPage();
  const homeClasses = homeStyles();
  const navigate = useNavigate();
  const intl = useIntl();

  setLayoutSubheader({
    title: id ? fm('USER.EDIT.TITLE') : fm('USER.ADD.TITLE'),
    breadcrumb: usersBreadCrumbs(intl, type || '', clearPagination),
  });
  setLayoutFooter({ show: true });


  useEffect(() => {
    if (id) fetch(id);
  }, [id]);

  useEffect(() => {
    if((addSuccess || editSuccess) && setUser){
      setUser('')
      setUserOpen(false)
    }
  }, [addSuccess, editSuccess])

  // formik
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormikUserEdit(id, user, edit, add, setUserOpen, page);

  useEffect(() => {
    if (byIdError) {
      navigate(-1);
      clearFetchUser();
    }
  }, [byIdError]);

  // clean up
  useEffect(() => {
    return () => {
      clearFetchUser();
      clearDelUser();
      clearEdit();
    };
  }, []);

  if (loading) return <Preloader />;

  return (
    <>
      <Card className={homeClasses.container}>
        <form className={homeClasses.form} onSubmit={handleSubmit} autoComplete='off'>
          <TextField
            type='text'
            label='Name'
            margin='normal'
            name='firstName'
            value={values.firstName}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.firstName && errors.firstName}
            error={Boolean(touched.firstName && errors.firstName)}
          />

          <TextField
            type='text'
            label='Surname'
            margin='normal'
            name='lastName'
            value={values.lastName || ''}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.lastName && errors.lastName}
            error={Boolean(touched.lastName && errors.lastName)}
          />

          <TextField
            type='text'
            label='Email'
            margin='normal'
            name='email'
            value={values.email || ''}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.email && errors.email}
            error={Boolean(touched.email && errors.email)}
          />

          {values.role !== 'ADMIN' && (
            <TextField
              type='text'
              label='CSB Cliend ID'
              margin='normal'
              name='csbClientId'
              value={values.csbClientId || ''}
              variant='outlined'
              onBlur={handleBlur}
              onChange={handleChange}
              helperText={touched.csbClientId && errors.csbClientId}
              error={Boolean(touched.csbClientId && errors.csbClientId)}
            />
          )}

          <TextField
            type='text'
            label='Company Name'
            margin='normal'
            name='companyName'
            value={values.companyName || ''}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.companyName && errors.companyName}
            error={Boolean(touched.companyName && errors.companyName)}
          />

          <TextField
            type='text'
            label='Company Address'
            margin='normal'
            name='companyAddress'
            value={values.companyAddress || ''}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.companyAddress && errors.companyAddress}
            error={Boolean(touched.companyAddress && errors.companyAddress)}
          />

          {values.role !== 'ADMIN' && (
            <TextField
              type='text'
              label='User Commission,%'
              margin='normal'
              name='platformFee'
              value={values.platformFee}
              variant='outlined'
              onBlur={handleBlur}
              onChange={handleChange}
              helperText={touched.platformFee && errors.platformFee}
              error={Boolean(touched.platformFee && errors.platformFee)}
            />
          )}

          <TextField
            select
            label='User Role'
            margin='normal'
            name='role'
            value={values.role}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
          >
            <MenuItem value='USER'>User</MenuItem>
            <MenuItem value='ADMIN'>Admin</MenuItem> 
          </TextField>

          <div className={classes.actions}>
            <ButtonWithLoader
              disabled={editLoading}
              loading={editLoading}
              fullHeight
            >
              {id ? 'Save' : 'Confirm'}
            </ButtonWithLoader>
          </div>
        </form>
      </Card>

      <Modal
        open={open}
        onClose={delUserLoading ? () => {} : () => setOpen(false)}
        title={fm('USER.DELETE.TITLE')}
        loading={delUserLoading}
        content={delUserLoading ? undefined : fm('USER.DELETE.TEXT')}
        actions={
          delUserLoading
            ? undefined
            : [
                {
                  title: fm('COMMON.BUTTON.CANCEL'),
                  onClick: () => setOpen(false),
                },
                {
                  title: fm('COMMON.BUTTON.DELETE'),
                  onClick: () => id && delUser(id),
                },
              ]
        }
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    user: state.users.user,
    page: state.users.page,
    loading: state.users.byIdLoading,
    editSuccess: state.users.editSuccess,
    editLoading: state.users.editLoading,
    editError: state.users.editError,
    byIdSuccess: state.users.byIdSuccess,
    byIdError: state.users.byIdError,
    delUserLoading: state.users.delUserLoading,
    delUserSuccess: state.users.delUserSuccess,
    delUserError: state.users.delUserError,
    addSuccess: state.users.addSuccess,
    addUserId: state.users.addUserId,
  }),
  {
    fetch: usersActions.fetchByIdRequest,
    clearFetchUser: usersActions.clearFetchById,
    clearEdit: usersActions.clearEdit,
    clearDelUser: usersActions.clearDelUser,
    add: usersActions.addRequest,
    edit: usersActions.editRequest,
    delUser: usersActions.delUserRequest,
    clearPagination: usersActions.clearPagination,
    clearAddUserId: usersActions.clearAddUserId,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(UsersEditPage);
